import { z } from 'zod';
import { schemas, type ProjectStatusValue, type ProjectsView } from '@kreart/shared';

export interface ProjectsListSearch {
  /** Which section of the list: the everyday one, "Arhivă", or "Proiecte șterse". */
  view?: ProjectsView;
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ProjectStatusValue[];
  clientId?: string;
  agentId?: string[];
  year?: number[];
  month?: number[];
  upcomingDeadline?: boolean;
  assignedToMe?: boolean;
  includeDeleted?: boolean;
}

const boolLoose = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'string' ? v === 'true' : v));

const statusEnum = z.enum([
  'IN_PROGRESS',
  'ON_HOLD',
  'SENT_TO_CLIENT',
  'REOFFER',
  'ACCEPTED',
  'IN_WARRANTY',
  'ARCHIVED',
  'REJECTED',
]);

const viewEnum = z.enum(['ACTIVE', 'ARCHIVED', 'DELETED']);

const raw = z
  .object({
    view: viewEnum.optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().trim().min(1).max(100).optional(),
    status: z
      .union([statusEnum, z.array(statusEnum)])
      .transform((v) => (Array.isArray(v) ? v : [v]))
      .optional(),
    clientId: z.string().min(1).optional(),
    agentId: z
      .union([z.string().min(1), z.array(z.string().min(1))])
      .transform((v) => (Array.isArray(v) ? v : [v]))
      .optional(),
    year: z
      .union([
        z.coerce.number().int().min(2000).max(2100),
        z.array(z.coerce.number().int().min(2000).max(2100)),
      ])
      .transform((v) => (Array.isArray(v) ? v : [v]))
      .optional(),
    month: z
      .union([
        z.coerce.number().int().min(1).max(12),
        z.array(z.coerce.number().int().min(1).max(12)),
      ])
      .transform((v) => (Array.isArray(v) ? v : [v]))
      .optional(),
    upcomingDeadline: boolLoose.optional(),
    assignedToMe: boolLoose.optional(),
    includeDeleted: boolLoose.optional(),
  })
  .passthrough();

export function parseProjectsListSearch(
  input: Record<string, unknown>,
  opts: { isAdmin: boolean },
): ProjectsListSearch {
  const parsed = raw.safeParse(input);
  if (!parsed.success) return {};
  const v = parsed.data;
  const out: ProjectsListSearch = {};
  // "Proiecte șterse" is ADMIN-only, so a non-admin landing on that URL gets
  // the everyday list rather than an error they cannot act on.
  if (v.view === 'ARCHIVED') out.view = 'ARCHIVED';
  if (v.view === 'DELETED' && opts.isAdmin) out.view = 'DELETED';
  if (v.page !== undefined && v.page !== 1) out.page = v.page;
  if (v.pageSize !== undefined && v.pageSize !== 25) out.pageSize = v.pageSize;
  if (v.search) out.search = v.search;
  if (v.status?.length) out.status = v.status;
  if (v.clientId) out.clientId = v.clientId;
  if (v.agentId?.length) out.agentId = v.agentId;
  if (v.year?.length) out.year = v.year;
  if (v.month?.length) out.month = v.month;
  if (v.upcomingDeadline) out.upcomingDeadline = true;
  if (v.assignedToMe) out.assignedToMe = true;
  if (v.includeDeleted && opts.isAdmin) out.includeDeleted = true;
  return out;
}

export function toListQuery(search: ProjectsListSearch) {
  return schemas.listProjectsQuery.parse({
    view: search.view ?? 'ACTIVE',
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 25,
    search: search.search,
    status: search.status,
    clientId: search.clientId,
    agentId: search.agentId,
    year: search.year,
    month: search.month,
    upcomingDeadline: search.upcomingDeadline,
    assignedToMe: search.assignedToMe,
    includeDeleted: search.includeDeleted ?? false,
  });
}
